'use client';

import type { sessionsHistoryInterface } from '@/types/history-types';
import ApiService, { type ApiResponse } from './api-services';

export enum SessionType {
  CHAT = 'chat',
  SEARCH = 'search',
  DOCUMENT_ANALYSIS = 'document_analysis',
  DOCUMENT_GENERATION = 'document_generation',
}

// Per-grant access level for group chat (#2607): 'read' = view only,
// 'read_write' = may send messages that drive @Lex.
export type SharePermission = 'read' | 'read_write';

export interface SessionShareGrant {
  session_id: string;
  grantee_type: 'user' | 'group';
  grantee_id: string;
  granted_by: string;
  granted_at: string;
  org_id: string;
  permission: SharePermission;
}

export interface SessionSharesResponse {
  session_id: string;
  grants: SessionShareGrant[];
  total: number;
}

// A resolved participant in a session's group chat (GET /sessions/{id}/members).
export interface SessionMember {
  grantee_type: 'owner' | 'user' | 'group';
  grantee_id: string;
  role: 'owner' | 'member';
  permission: SharePermission;
  name: string | null;
  email: string | null;
  /**
   * Profile avatar (Google/OAuth picture) from auth.users metadata; null for
   * group grants and avatar-less users — consumers fall back to initials.
   */
  avatar_url?: string | null;
}

export interface SessionMembersResponse {
  session_id: string;
  members: SessionMember[];
}

// One hit from GET /sessions/search (#6067) — a chat session whose message
// content matched the query, with a pgroonga-highlighted snippet. `snippet` is
// server-generated HTML (escaped source + <span class="keyword"> spans).
export interface ChatSearchResult {
  id: number;
  session_id: string;
  // Nullable: the DB column `sessions.title` has no NOT NULL constraint; newly
  // created sessions get a generated title asynchronously, so a hit may arrive
  // with null. Call sites must coalesce (e.g. `hit.title ?? ''`) before storing
  // into a non-nullable field.
  title: string | null;
  url: string | null;
  created_at: string;
  snippet: string | null;
}

class sessionServices extends ApiService {
  constructor() {
    super('/sessions');
  }

  getSessionId(
    type: SessionType,
    thinkingMode = 'fast',
    // Optional Spis (case) scope (Phase 4 claims-memory, D-11) — threaded
    // through to `POST /sessions`' already-accepted `case_id` field so
    // `session.case_id` is populated regardless of chat-entry point. Omitted
    // entirely (not sent as `null`/`undefined`) for a plain general chat.
    caseId?: number | null
  ): ApiResponse<{ session: string }> {
    const query = window.btoa(window.location.href);
    return this.postData('', {
      query,
      type,
      thinking_mode: thinkingMode,
      ...(caseId != null && { case_id: caseId }),
    });
  }

  updateSessionUrl({
    session_id,
    url = window.location.href,
  }: {
    session_id: string;
    url?: string;
  }): ApiResponse {
    return this.postData('/update-url', {
      session_id,
      url,
    });
  }

  getSessionHistory(): ApiResponse<sessionsHistoryInterface> {
    return this.getData('/history');
  }

  // Full-text/content search of the user's own chat sessions (#6067). Backs the
  // ⌘K palette's content search; owner-scoped server-side, blank q → empty.
  searchSessions(q: string, limit = 20): ApiResponse<ChatSearchResult[]> {
    return this.getData('/search', { params: { q, limit } });
  }

  shareSession({
    session_id,
    is_shared,
  }: {
    session_id: string;
    is_shared: boolean;
  }): ApiResponse {
    return this.postData('/share', {
      session_id,
      is_shared,
    });
  }

  getSharedSession(session_id: string): ApiResponse<{ is_shared: boolean }> {
    return this.getData(`/share/${session_id}`);
  }

  /**
   * Fork a shared (or owned) session into the current user's account.
   * Returns the new, independent session id to navigate to. Requires auth.
   */
  forkSession(session_id: string): ApiResponse<{ session: string }> {
    return this.postData(`/${session_id}/fork`, {});
  }

  deleteSession(session_id: string): ApiResponse {
    return this.deleteData(`/orphan/${session_id}`, {});
  }

  // Per-session perspective storage (Issue #6886 PR2).
  // Returns the stored perspective, or a profession-based default when not yet set.
  getSessionPerspective(sessionId: string): ApiResponse<string> {
    return this.getData(`/${sessionId}/perspective`);
  }

  // Persist the selected perspective on the session row.
  updateSessionPerspective(
    sessionId: string,
    perspective: string
  ): ApiResponse {
    return this.patchData(`/${sessionId}/perspective`, { perspective });
  }

  // Per-session context-chip scope (#8064). Returns the chip ids the session's
  // last conversation turn was scoped to: null = never persisted (legacy/new
  // session), [] = last turn explicitly unscoped, [ids] = persisted scope
  // already filtered server-side to the caller's live chips.
  getSessionContextChips(sessionId: string): ApiResponse<number[] | null> {
    return this.getData(`/${sessionId}/context-chips`);
  }

  getSessionShares(sessionId: string): ApiResponse<SessionSharesResponse> {
    return this.getData(`/${sessionId}/shares`);
  }

  addSessionShare(
    sessionId: string,
    granteeType: 'user' | 'group',
    granteeId: string,
    // Group chat (#2607): defaults to read-only so a member is never silently
    // write-capable — the owner opts them up to 'read_write' explicitly.
    permission: SharePermission = 'read'
  ): ApiResponse<SessionShareGrant> {
    return this.postData(`/${sessionId}/shares`, {
      grantee_type: granteeType,
      grantee_id: granteeId,
      permission,
    });
  }

  // Change an existing grant's permission (read <-> read_write). Owner/admin only.
  updateSharePermission(
    sessionId: string,
    granteeType: 'user' | 'group',
    granteeId: string,
    permission: SharePermission
  ): ApiResponse<SessionShareGrant> {
    return this.patchData(`/${sessionId}/shares/${granteeType}/${granteeId}`, {
      permission,
    });
  }

  // Resolved participant roster for a session's group chat (owner + grantees).
  getSessionMembers(sessionId: string): ApiResponse<SessionMembersResponse> {
    return this.getData(`/${sessionId}/members`);
  }

  removeSessionShare(
    sessionId: string,
    granteeType: string,
    granteeId: string
  ): ApiResponse<void> {
    return this.deleteData(
      `/${sessionId}/shares/${granteeType}/${granteeId}`,
      {}
    );
  }
}

export default new sessionServices();
